import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useCspApi } from "@perfectserve/http-ps";
import { setAccountTimezones, clearAccountTimezones } from "../utils/timezoneUtils";
import { addLocalDatesToInteractions } from "../utils/interactionTimezoneProcessor";
const API_CALL_DELAY_MS = 500;
const FETCH_DEBOUNCE_MS = 1000;
const PAGINATION_BATCH_SIZE = 4000;
const INITIAL_FETCH_LIMIT = 10000;
const PAGINATION_DELAY_MS = 100;
const MAX_PAGINATION_PAGES = 50;
const createSelectionKey = (selectedAccounts, selectedUsers, dateRange, minimumCallDurationSeconds, excludeHangupsAndDisconnects) => {
    const accountIds = selectedAccounts
        .map((a) => a.id)
        .sort()
        .join(",");
    const userIds = selectedUsers
        .map((u) => u.id)
        .sort()
        .join(",");
    const daysKey = dateRange.selectedDays ? dateRange.selectedDays.sort().join(",") : "";
    const dateKey = `${dateRange.startDate}|${dateRange.endDate}|${dateRange.startTime || ""}|${dateRange.endTime || ""}|${daysKey}`;
    const settingsKey = `${minimumCallDurationSeconds}|${excludeHangupsAndDisconnects}`;
    return `${accountIds}::${userIds}::${dateKey}::${settingsKey}`;
};
export const useInteractionsData = ({ dateRange, selectedAccounts, selectedUsers, viewSettings, autoFetch = true, minimumCallDurationSeconds = 10, excludeHangupsAndDisconnects = true, }) => {
    const { get } = useCspApi();
    const [allInteractionsByAccount, setAllInteractionsByAccount] = useState({});
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [entityLoadingStates, setEntityLoadingStates] = useState({});
    const lastFetchedKeyRef = useRef("");
    const hasValidSelection = useMemo(() => selectedAccounts.length > 0 || selectedUsers.length > 0, [selectedAccounts, selectedUsers]);
    const hasValidDateRange = useMemo(() => dateRange.startDate && dateRange.endDate, [dateRange]);
    const applyFilters = useCallback((interactions) => {
        let filtered = interactions;
        if (minimumCallDurationSeconds && minimumCallDurationSeconds > 0) {
            filtered = filtered.filter((interaction) => interaction.CallDurationSeconds !== undefined &&
                interaction.CallDurationSeconds >= minimumCallDurationSeconds);
        }
        if (dateRange.startTime && dateRange.endTime) {
            const shiftStartTime = dateRange.startTime;
            const shiftEndTime = dateRange.endTime;
            filtered = filtered.filter((interaction) => {
                const dateString = interaction.InteractionStartDate || interaction.InteractionStartDateUtc;
                if (!dateString)
                    return false;
                const timePart = dateString.split("T")[1];
                if (!timePart)
                    return false;
                const interactionTime = timePart.substring(0, 5);
                if (shiftStartTime > shiftEndTime) {
                    return interactionTime >= shiftStartTime || interactionTime <= shiftEndTime;
                }
                return interactionTime >= shiftStartTime && interactionTime <= shiftEndTime;
            });
        }
        if (dateRange.selectedDays && dateRange.selectedDays.length > 0) {
            filtered = filtered.filter((interaction) => {
                const dateString = interaction.InteractionStartDate || interaction.InteractionStartDateUtc;
                if (!dateString) {
                    return false;
                }
                const date = new Date(dateString);
                if (isNaN(date.getTime())) {
                    return false;
                }
                const dayOfWeek = date.getDay();
                return dateRange.selectedDays.includes(dayOfWeek);
            });
        }
        return filtered;
    }, [minimumCallDurationSeconds, dateRange.selectedDays, dateRange.startTime, dateRange.endTime]);
    const filteredInteractionsByAccount = useMemo(() => {
        const filtered = {};
        for (const [entityId, interactions] of Object.entries(allInteractionsByAccount)) {
            filtered[entityId] = applyFilters(interactions);
        }
        return filtered;
    }, [allInteractionsByAccount, applyFilters]);
    const allInteractions = useMemo(() => {
        const interactions = Object.values(filteredInteractionsByAccount).flat();
        if (interactions.length > 0 && dateRange.selectedDays && dateRange.selectedDays.length > 0) {
            const sample = interactions[0];
            console.log("Sample interaction date info:", {
                InteractionStartDateUtc: sample === null || sample === void 0 ? void 0 : sample.InteractionStartDateUtc,
                InteractionStartDate: sample === null || sample === void 0 ? void 0 : sample.InteractionStartDate,
                utcDay: new Date((sample === null || sample === void 0 ? void 0 : sample.InteractionStartDateUtc) || "").getUTCDay(),
                localDay: new Date((sample === null || sample === void 0 ? void 0 : sample.InteractionStartDate) || (sample === null || sample === void 0 ? void 0 : sample.InteractionStartDateUtc) || "").getDay(),
            });
        }
        return interactions;
    }, [filteredInteractionsByAccount, dateRange.selectedDays]);
    const isBufferOverflowError = (error) => {
        var _a;
        if ((_a = error === null || error === void 0 ? void 0 : error.body) === null || _a === void 0 ? void 0 : _a.Message) {
            const message = error.body.Message;
            return (message.includes("AsyncEnumerableReader") &&
                message.includes("reached the configured maximum size of the buffer"));
        }
        return false;
    };
    const fetchInteractionsPage = async (encodedFilter, skip, top) => {
        const endpoint = `/api/reporting/interactions?$filter=${encodedFilter}&$orderby=InteractionStartDateUtc desc&$top=${top}&$skip=${skip}`;
        const response = await get(endpoint);
        return response.data;
    };
    const fetchInteractionsPaginated = async (encodedFilter) => {
        const allInteractions = [];
        let skip = 0;
        let hasMore = true;
        let pageCount = 0;
        while (hasMore && pageCount < MAX_PAGINATION_PAGES) {
            const pageData = await fetchInteractionsPage(encodedFilter, skip, PAGINATION_BATCH_SIZE);
            for (const interaction of pageData) {
                allInteractions.push(interaction);
            }
            pageCount++;
            if (pageData.length < PAGINATION_BATCH_SIZE) {
                hasMore = false;
            }
            else {
                skip += PAGINATION_BATCH_SIZE;
                await new Promise((resolve) => setTimeout(resolve, PAGINATION_DELAY_MS));
            }
        }
        return allInteractions;
    };
    const fetchInteractionsForEntity = async (entityId, entityType) => {
        var _a, _b;
        const requestOptions = {
            accountIds: entityType === "account" ? [entityId] : [],
            userIds: entityType === "user" ? [entityId] : [],
            startDate: new Date(dateRange.startDate),
            endDate: new Date(dateRange.endDate),
            minimumCallDurationSeconds: (_a = viewSettings === null || viewSettings === void 0 ? void 0 : viewSettings.minimumCallDurationSeconds) !== null && _a !== void 0 ? _a : minimumCallDurationSeconds,
            excludeHangupsAndDisconnects: (_b = viewSettings === null || viewSettings === void 0 ? void 0 : viewSettings.excludeHangupsAndDisconnects) !== null && _b !== void 0 ? _b : excludeHangupsAndDisconnects,
        };
        const filter = buildODataFilter(requestOptions);
        const encodedFilter = encodeURIComponent(filter);
        try {
            return await fetchInteractionsPage(encodedFilter, 0, INITIAL_FETCH_LIMIT);
        }
        catch (error) {
            if (isBufferOverflowError(error)) {
                return await fetchInteractionsPaginated(encodedFilter);
            }
            throw error;
        }
    };
    const buildODataFilter = (options) => {
        const filters = [];
        if (options.accountIds && options.accountIds.length > 0) {
            const accountFilter = options.accountIds.map((id) => `ToFilterObjectAccountId eq '${id}'`).join(" or ");
            filters.push(`(${accountFilter})`);
        }
        if (options.userIds && options.userIds.length > 0) {
            const userFilter = options.userIds.map((id) => `ToObjectId eq '${id}'`).join(" or ");
            filters.push(`(${userFilter})`);
        }
        const formatTimeForOData = (date) => {
            return date.toISOString();
        };
        const startDateFormatted = formatTimeForOData(options.startDate);
        const endDateFormatted = formatTimeForOData(options.endDate);
        filters.push(`InteractionStartDateUtc ge ${startDateFormatted} and InteractionStartDateUtc le ${endDateFormatted}`);
        return filters.join(" and ");
    };
    const fetchData = useCallback(async () => {
        if (!hasValidSelection || !hasValidDateRange) {
            return;
        }
        setLoading(true);
        setError(null);
        try {
            const newInteractionsByAccount = {};
            clearAccountTimezones();
            const accountTimezoneMap = {};
            for (const account of selectedAccounts) {
                if (account.timeZone) {
                    accountTimezoneMap[account.id] = account.timeZone;
                }
            }
            setAccountTimezones(accountTimezoneMap);
            const allEntityIds = [...selectedAccounts.map((a) => a.id), ...selectedUsers.map((u) => u.id)];
            const initialLoadingStates = {};
            allEntityIds.forEach((id) => {
                initialLoadingStates[id] = true;
            });
            setEntityLoadingStates(initialLoadingStates);
            for (let i = 0; i < selectedAccounts.length; i++) {
                const account = selectedAccounts[i];
                if (!account)
                    continue;
                try {
                    const rawInteractions = await fetchInteractionsForEntity(account.id, "account");
                    const interactions = addLocalDatesToInteractions(rawInteractions, accountTimezoneMap);
                    newInteractionsByAccount[account.id] = interactions;
                    setAllInteractionsByAccount({ ...newInteractionsByAccount });
                    setEntityLoadingStates((prev) => ({
                        ...prev,
                        [account.id]: false,
                    }));
                    if (i < selectedAccounts.length - 1) {
                        await new Promise((resolve) => setTimeout(resolve, API_CALL_DELAY_MS));
                    }
                }
                catch (accountError) {
                    console.error(`Error fetching interactions for account ${account.id}:`, accountError);
                    const errorMessage = accountError instanceof Error
                        ? accountError.message
                        : `Failed to fetch interactions for account ${account.name || account.id}`;
                    setError(errorMessage);
                    newInteractionsByAccount[account.id] = [];
                    setAllInteractionsByAccount({ ...newInteractionsByAccount });
                    setEntityLoadingStates((prev) => ({
                        ...prev,
                        [account.id]: false,
                    }));
                }
            }
            for (let i = 0; i < selectedUsers.length; i++) {
                const user = selectedUsers[i];
                if (!user)
                    continue;
                try {
                    const rawInteractions = await fetchInteractionsForEntity(user.id, "user");
                    const interactions = addLocalDatesToInteractions(rawInteractions, accountTimezoneMap);
                    newInteractionsByAccount[user.id] = interactions;
                    setAllInteractionsByAccount({ ...newInteractionsByAccount });
                    setEntityLoadingStates((prev) => ({
                        ...prev,
                        [user.id]: false,
                    }));
                    if (i < selectedUsers.length - 1) {
                        await new Promise((resolve) => setTimeout(resolve, API_CALL_DELAY_MS));
                    }
                }
                catch (userError) {
                    console.error(`Error fetching interactions for user ${user.id}:`, userError);
                    const errorMessage = userError instanceof Error
                        ? userError.message
                        : `Failed to fetch interactions for user ${user.displayName || user.id}`;
                    setError(errorMessage);
                    newInteractionsByAccount[user.id] = [];
                    setAllInteractionsByAccount({ ...newInteractionsByAccount });
                    setEntityLoadingStates((prev) => ({
                        ...prev,
                        [user.id]: false,
                    }));
                }
            }
            setAllInteractionsByAccount(newInteractionsByAccount);
        }
        catch (fetchError) {
            const errorMessage = fetchError instanceof Error ? fetchError.message : "Unknown error occurred";
            setError(errorMessage);
        }
        finally {
            setLoading(false);
        }
    }, [
        dateRange.startDate,
        dateRange.endDate,
        dateRange.startTime,
        dateRange.endTime,
        selectedAccounts,
        selectedUsers,
        hasValidSelection,
        hasValidDateRange,
        viewSettings === null || viewSettings === void 0 ? void 0 : viewSettings.minimumCallDurationSeconds,
        viewSettings === null || viewSettings === void 0 ? void 0 : viewSettings.excludeHangupsAndDisconnects,
        minimumCallDurationSeconds,
        excludeHangupsAndDisconnects,
    ]);
    const clearData = useCallback(() => {
        setAllInteractionsByAccount({});
        setError(null);
        setEntityLoadingStates({});
        lastFetchedKeyRef.current = "";
    }, []);
    const currentSelectionKey = useMemo(() => createSelectionKey(selectedAccounts, selectedUsers, dateRange, minimumCallDurationSeconds, excludeHangupsAndDisconnects), [selectedAccounts, selectedUsers, dateRange, minimumCallDurationSeconds, excludeHangupsAndDisconnects]);
    useEffect(() => {
        if (!autoFetch) {
            return;
        }
        if (!hasValidSelection || !hasValidDateRange) {
            if (Object.keys(allInteractionsByAccount).length > 0) {
                setAllInteractionsByAccount({});
                setError(null);
                setEntityLoadingStates({});
                lastFetchedKeyRef.current = "";
                setLoading(false);
            }
            return;
        }
        const selectionUnchanged = currentSelectionKey === lastFetchedKeyRef.current;
        if (selectionUnchanged) {
            return;
        }
        const hasExistingData = Object.keys(allInteractionsByAccount).length > 0;
        if (hasExistingData && !selectionUnchanged) {
            setAllInteractionsByAccount({});
            setEntityLoadingStates({});
        }
        setLoading(true);
        const keyAtFetchTime = currentSelectionKey;
        const timer = setTimeout(() => {
            fetchData()
                .then(() => {
                lastFetchedKeyRef.current = keyAtFetchTime;
            })
                .catch(() => {
            });
        }, FETCH_DEBOUNCE_MS);
        return () => {
            clearTimeout(timer);
            setLoading(false);
        };
    }, [autoFetch, hasValidSelection, hasValidDateRange, currentSelectionKey]);
    return {
        allInteractionsByAccount: filteredInteractionsByAccount,
        allInteractions,
        loading,
        error,
        entityLoadingStates,
        fetchData,
        clearData,
    };
};
//# sourceMappingURL=useInteractionsData.js.map